import type { Scene } from '../types';
import { ClimateChart } from '../components/ClimateChart';

export class SceneClimate implements Scene {
  readonly panel: HTMLElement;
  private chart: ClimateChart | null = null;
  private built = false;

  constructor(
    root: HTMLElement,
    private readonly csvText: string,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'climate';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Baseline Climate</div>
        <div class="sm-panel-sub">AgERA5 · 0.1° · daily · last 5 years · Chewore–Sapi study area</div>
      </div>
      <div class="sm-headline">
        Understanding climate seasonality is the baseline for detecting deviation —
        drought, flood, or temperature extremes — that signal ecological change
        before it appears in field surveys.
      </div>
      <div class="sm-panel-narrative">
        The Chewore–Sapi landscape sits at the intersection of the Congo Air Boundary
        and the Intertropical Convergence Zone, creating a pronounced two-season
        climate: a hot wet season (November–April) and a cool dry season (May–October).
        This seasonality drives phenology, fire risk, and water availability — the
        primary controls on which species survive where. Each panel shows the
        climatological mean across 2001–2024. Hover any chart for exact values.
        The <span class="sm-cc-inline-swatch" style="background:#D0B39C;"></span> escarpment,
        <span class="sm-cc-inline-swatch" style="background:#97B8CB;"></span> river, and
        <span class="sm-cc-inline-swatch" style="background:#3F8533;"></span> seasonal river
        vegetation reference points are compared for each variable.
        Hover any chart for exact values;
        hover the <span class="sm-cc-info-icon-inline">?</span> on each card for an explanation.
        The dark reference line shows the five-year climatological mean,
        providing a stable recent baseline against which individual seasons
        and anomalies are compared.
      </div>
      <div class="sm-climate-grid" id="sm-climate-grid"></div>
    `;
    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
  }

  onLeave(): void { /* chart stays in DOM */ }

  private build(): void {
    const grid = this.panel.querySelector<HTMLElement>('#sm-climate-grid')!;
    this.chart = new ClimateChart(grid, this.csvText);
  }
}
